import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';

import useCurrency from 'hooks/useCurreny';
import { cn } from 'utils/cn';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

const SupplyPlanTableColumns = ({ isPDF }: {
  isPDF?: boolean;
}) => {
  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const { onlyMt, onlyAc } = useCroptimizer();

  return (
    <thead className="bg-muted text-left bg-[#FAFAFA]">
      <tr>
        <th colSpan={isPDF ? 13 : 14} className='p-2 border-none'></th>
      </tr>

      <tr>
        <th
          className={cn('border-none pb-4 pt-0 text-center text-black font-medium', isPDF ? 'px-1 text-xs': 'pl-6 pr-1 text-sm')}
          colSpan={2}
        >
          {t('fieldName')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 pb-4 pt-0 text-center text-black font-medium', isPDF? 'text-xs': 'text-sm')}>
          {t('cropRotationReminder')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 pb-4 pt-0 text-center text-black font-medium', isPDF? 'text-xs': 'text-sm')} colSpan={4}>
          {t('omyaSolutions')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 pb-4 pt-0 text-center text-black font-medium', isPDF? 'text-xs': 'text-sm')} colSpan={3}>
          {t('soilPh')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] text-center text-black font-medium pr-6 pl-1 pb-4 pt-0 ', isPDF ? 'text-xs': 'text-sm')} colSpan={4}>
          {t('totalPerField')}
        </th>
      </tr>

      <tr>
        <th className="border-none p-0" colSpan={2}></th>
        <th className="border-b-0 border-l-[0.5px] border-l-[#CBCBCB] p-0"></th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('product')} <br /> {t('name')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('area')} <br />({onlyAc})
        </th>
        <th data-tooltip-id="dose" className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('dose')} <br />({onlyMt}/{onlyAc})
          {!isPDF && <span className="font-black text-[#007AC1]">ⓘ</span>}
          <Tooltip
            id="dose"
            place="bottom-start"
            className="tooltip"
            variant="light"
            noArrow
          >
            {t('doseTooltip')}
          </Tooltip>
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('total')} <br /> ({onlyMt})
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('current')} <br /> pH
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('limingStatus')}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('target')}
          <br /> pH
        </th>
        <th data-tooltip-id='roi' className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('gains')} <br /> ({currencyGreen})
          {!isPDF && <span className="font-black text-[#007AC1]">ⓘ</span>}
        </th>
        <th className={cn('border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]', isPDF ? 'text-xs': 'text-sm')}>
          {t('cost')} ({currencyGreen})
        </th>
        <th className={cn(
          'border-b-0 border-l-[0.5px] border-l-[#CBCBCB] px-1 py-1 text-center min-w-[80px]',
          { 'text-xs': isPDF },
          { 'text-sm': !isPDF },
        )} data-tooltip-id="roi">
          {t('roi')} <br /> ({currencyGreen})
          {!isPDF && <span className="font-black text-[#007AC1]">ⓘ</span>}
          <Tooltip
            id="roi"
            place="bottom-start"
            className="tooltip"
            variant="light"
            noArrow
          >
            {t('roiTooltip')}
          </Tooltip>
        </th>

        {!isPDF && (
          <th className="border-none"></th>
        )}
      </tr>
      <tr>
        <th colSpan={isPDF ? 13 : 14} className='p-2 border-none'></th>
      </tr>
    </thead>
  );
};

export default SupplyPlanTableColumns;
